import React, { createContext, useContext, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from './utils';

// Create context for select state
const SelectContext = createContext();

// Main Select component that provides context
const Select = ({ children, value, onValueChange, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || '');

  const handleValueChange = (newValue) => {
    setSelectedValue(newValue);
    if (onValueChange) {
      onValueChange(newValue);
    }
    setIsOpen(false);
  };

  return (
    <SelectContext.Provider value={{
      isOpen,
      setIsOpen,
      selectedValue,
      handleValueChange,
      ...props
    }}>
      <div className="relative">
        {children}
      </div>
    </SelectContext.Provider>
  );
};

// SelectTrigger component
const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => {
  const context = useContext(SelectContext);
  
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => context.setIsOpen(!context.isOpen)}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 opacity-50" />
    </button>
  );
});

// SelectValue component
const SelectValue = React.forwardRef(({ className, placeholder, ...props }, ref) => {
  const context = useContext(SelectContext);
  
  return (
    <span
      ref={ref}
      className={cn("block truncate", className)}
      {...props}
    >
      {context.selectedValue || placeholder}
    </span>
  );
});

// SelectContent component
const SelectContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const context = useContext(SelectContext);
  
  if (!context.isOpen) return null;
  
  return (
    <div
      ref={ref}
      className={cn(
        "absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white py-1 text-gray-950 shadow-lg",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

// SelectItem component
const SelectItem = React.forwardRef(({ className, children, value, ...props }, ref) => {
  const context = useContext(SelectContext);
  
  return (
    <div
      ref={ref}
      onClick={() => context.handleValueChange(value)}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center py-1.5 pl-3 pr-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        context.selectedValue === value && "bg-gray-100",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

Select.displayName = "Select";
SelectContent.displayName = "SelectContent";
SelectItem.displayName = "SelectItem";
SelectTrigger.displayName = "SelectTrigger";
SelectValue.displayName = "SelectValue";

export { Select, SelectContent, SelectItem, SelectTrigger, SelectValue };
